<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ t("exams.management") }}
            </h1>
            <router-link
                v-if="canCreateExam"
                to="/exams/create"
                class="btn btn-primary"
            >
                <Plus class="w-4 h-4 mr-1" />
                {{ t("exams.addExam") }}
            </router-link>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    :placeholder="t('exams.searchPlaceholder')"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Class Filter -->
                    <SearchableSelect
                        v-model="filters.class_id"
                        :options="classOptions"
                        :placeholder="t('exams.allClasses')"
                        :option-label="getClassLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchExams"
                    />

                    <!-- Subject Filter -->
                    <SearchableSelect
                        v-model="filters.subject_id"
                        :options="subjectOptions"
                        :placeholder="t('exams.allSubjects')"
                        :option-label="getSubjectLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchExams"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.status"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchExams"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        {{ t("exams.resetFilters") }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="examStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="9" />
        </div>

        <!-- Error State -->
        <div
            v-if="examStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ examStore.error }}
                    </p>
                    <button
                        @click="examStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!examStore.loading && !examStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("exams.examName") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("exams.subject") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("exams.class") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("common.name") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("exams.startTime") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("exams.endTime") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("exams.passingScore") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("common.status") }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                {{ t("common.actions") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="exam in examStore.exams"
                            :key="exam.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ exam.title }}
                                </div>
                                <div
                                    v-if="exam.description"
                                    class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                >
                                    {{ exam.description }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ exam.class_subject?.subject?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ exam.class_subject?.class?.grade_level }}
                                -
                                {{ exam.class_subject?.class?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ exam.teacher?.user?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDateTime(exam.start_time) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDateTime(exam.end_time) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ exam.passing_score || "-" }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    :class="getStatusBadgeClass(exam.status)"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                >
                                    {{ t(`exams.${exam.status}`) }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <div class="flex items-center space-x-2">
                                    <!-- Status Update Dropdown -->
                                    <select
                                        v-model="exam.status"
                                        @change="
                                            updateExamStatus(exam.id, $event)
                                        "
                                        :disabled="updatingStatus === exam.id"
                                        class="text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white px-2 py-1"
                                    >
                                        <option
                                            v-if="updatingStatus === exam.id"
                                            value=""
                                            disabled
                                        >
                                            {{ t("exams.updating") }}
                                        </option>
                                        <option value="draft">
                                            {{ t("exams.draft") }}
                                        </option>
                                        <option value="scheduled">
                                            {{ t("exams.scheduled") }}
                                        </option>
                                        <option value="ongoing">
                                            {{ t("exams.ongoing") }}
                                        </option>
                                        <option value="closed">
                                            {{ t("exams.completed") }}
                                        </option>
                                        <option value="archived">
                                            {{ t("exams.archived") }}
                                        </option>
                                    </select>

                                    <!-- Edit Button -->
                                    <router-link
                                        :to="`/exams/${exam.id}/edit`"
                                        title="Edit Exam"
                                        class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </router-link>

                                    <!-- View Attempts/Grade Button -->
                                    <router-link
                                        :to="getResultsRoute(exam)"
                                        title="View Results"
                                        class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                    >
                                        <BarChart3 class="w-4 h-4" />
                                    </router-link>

                                    <!-- Delete Button -->
                                    <button
                                        v-if="canDeleteExam"
                                        @click="confirmDelete(exam)"
                                        title="Delete Exam"
                                        class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="examStore.exams.length === 0"
                :icon="FileText"
                :title="t('exams.noExamsFound')"
                :description="t('exams.getStarted')"
            >
                <router-link
                    v-if="canCreateExam"
                    to="/exams/create"
                    class="btn btn-primary"
                >
                    {{ t("exams.addExam") }}
                </router-link>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="examStore.meta"
                :per-page="examStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
        <div
            v-if="showDeleteModal"
            class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
            @click.self="showDeleteModal = false"
        >
            <div
                class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800"
            >
                <div class="mt-3 text-center">
                    <h3
                        class="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                    >
                        {{ t("messages.confirmDelete") }}
                    </h3>
                    <div class="mt-2 px-7 py-3">
                        <p class="text-sm text-gray-500 dark:text-gray-400">
                            {{ deleteModalMessage }}
                        </p>
                    </div>
                    <div class="items-center px-4 py-3">
                        <button
                            @click="showDeleteModal = false"
                            class="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200"
                        >
                            {{ t("common.cancel") }}
                        </button>
                        <button
                            @click="deleteExam"
                            class="ml-3 px-4 py-2 bg-danger-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-danger-700"
                        >
                            {{ t("common.delete") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useExamStore } from "@/stores/examStore";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/authStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import classSchoollApi from "@/api/classSchooll";
import subjectApi from "@/api/subject";
import examApi from "@/api/exam";
import type { Exam } from "@/api/exam";
import type { ClassSchooll } from "@/api/classSchooll";
import type { Subject } from "@/api/subject";
import {
    RotateCcw,
    Pencil,
    BarChart3,
    Trash2,
    FileText,
    Plus,
} from "@lucide/vue";

const examStore = useExamStore();
const { t } = useI18n();
const authStore = useAuthStore();

// Role-based access control
const canCreateExam = computed(() => authStore.hasPermission("exams.create"));

const canDeleteExam = computed(
    () =>
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator"),
);

// State
const searchQuery = ref("");
const filters = ref({
    class_id: "",
    subject_id: "",
    status: null as string | null,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedExam = ref<Exam | null>(null);
const isEditing = ref(false);
const examToDelete = ref<Exam | null>(null);
const updatingStatus = ref<string | null>(null);
// Types for options with "Select All" option
type ClassOption = ClassSchooll | { id: ""; grade_level: string; name: string };
type SubjectOption = Subject | { id: ""; name: string; code: string };

const classOptions = ref<ClassOption[]>([]);
const subjectOptions = ref<SubjectOption[]>([]);

// Delete modal properties
const deleteModalTitle = computed(() => t("messages.confirmDelete"));
const deleteModalMessage = computed(() => t("messages.deleteConfirmExam"));

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.class_id !== "" ||
        filters.value.subject_id !== "" ||
        filters.value.status !== null ||
        examStore.page !== 1 ||
        examStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = computed(() => [
    { value: null, label: t("exams.allStatuses") },
    { value: "draft", label: t("exams.draft") },
    { value: "scheduled", label: t("exams.scheduled") },
    { value: "ongoing", label: t("exams.ongoing") },
    { value: "closed", label: t("exams.completed") },
    { value: "archived", label: t("exams.archived") },
]);

// Methods
const handlePerPageChange = (value: number) => {
    examStore.perPage = value;
    examStore.page = 1;
    fetchExams();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { class_id: "", subject_id: "", status: null };
    examStore.page = 1;
    examStore.perPage = 10;
    fetchExams();
};

const fetchExams = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
        status: filters.value.status as
            | "draft"
            | "scheduled"
            | "ongoing"
            | "closed"
            | "archived"
            | undefined,
    };
    await examStore.fetchExams(searchFilters);
};

const fetchOptions = async () => {
    try {
        const [classesResponse, subjectsResponse] = await Promise.all([
            classSchoollApi.getAll({ per_page: 1000 }),
            subjectApi.getAll({ per_page: 1000 }),
        ]);
        // Add "Select All" option as the first element
        classOptions.value = [
            { id: "", grade_level: "", name: t("exams.allClasses") },
            ...(classesResponse.data?.items || []),
        ];
        subjectOptions.value = [
            { id: "", name: t("exams.allSubjects"), code: "" },
            ...(subjectsResponse.data?.items || []),
        ];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getClassLabel = (classOption: ClassOption): string => {
    if (classOption.id === "") return classOption.name;
    return `${classOption.grade_level} - ${classOption.name}`;
};

const getSubjectLabel = (subjectOption: SubjectOption): string => {
    if (subjectOption.id === "") return subjectOption.name;
    return `${subjectOption.name} (${subjectOption.code})`;
};

const debouncedSearch = useDebounce(fetchExams, 300);

const goToPage = (page: number) => {
    examStore.setPage(page);
    fetchExams();
};

const formatDateTime = (dateTime: string) => {
    if (!dateTime) return "-";
    return new Date(dateTime).toLocaleString();
};

const getStatusBadgeClass = (status: string) => {
    switch (status) {
        case "draft":
            return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
        case "scheduled":
            return "bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200";
        case "ongoing":
            return "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200";
        case "closed":
            return "bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200";
        case "archived":
            return "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200";
        default:
            return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
};

const getStatusLabel = (status: string) => {
    switch (status) {
        case "draft":
            return t("exams.draft");
        case "scheduled":
            return t("exams.scheduled");
        case "ongoing":
            return t("exams.ongoing");
        case "closed":
            return t("exams.completed");
        case "archived":
            return t("exams.archived");
        default:
            return status;
    }
};

const confirmDelete = (exam: Exam) => {
    selectedExam.value = exam;
    examToDelete.value = exam;
    showDeleteModal.value = true;
};

const updateExamStatus = async (examId: string, event: Event) => {
    const newStatus = (event.target as HTMLSelectElement).value as
        "draft" | "scheduled" | "ongoing" | "closed" | "archived";

    // DEBUG: Log the status update attempt
    console.log("DEBUG: updateExamStatus - Attempting to update status", {
        examId,
        newStatus,
        updateData: { status: newStatus },
    });

    try {
        updatingStatus.value = examId; // Set updating flag

        // DEBUG: Log the API call
        console.log(
            "DEBUG: updateExamStatus - Making API call to examApi.update",
        );

        const response = await examApi.updateStatus(examId, newStatus);

        // DEBUG: Log successful response
        console.log("DEBUG: updateExamStatus - API call successful", {
            response,
            updatedExam: response,
        });

        NotificationService.success(`Exam status changed to ${newStatus}`);
        updatingStatus.value = null; // Clear updating flag

        // Refresh the exams list to show updated status
        await fetchExams();
    } catch (error) {
        // DEBUG: Log the error details
        console.error("DEBUG: updateExamStatus - API call failed", {
            error,
            errorMessage:
                error instanceof Error ? error.message : String(error),
            errorResponse:
                error && typeof error === "object" && "response" in error
                    ? (error as any).response?.data
                    : undefined,
            examId,
            newStatus,
            updateData: { status: newStatus },
        });

        NotificationService.error("Failed to update exam status");
        updatingStatus.value = null;
    }
};

const deleteExam = async () => {
    if (!examToDelete.value) return;

    try {
        await examStore.deleteExam(examToDelete.value.id);
        showNotification("Exam deleted successfully", "success");
        showDeleteModal.value = false;
        examToDelete.value = null;
        selectedExam.value = null;
        await fetchExams(); // Refresh the list
    } catch (error) {
        console.error("Delete error:", error);
        showNotification("Failed to delete exam", "error");
    }
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchExams();
    fetchOptions();
});

// Methods for role-based routing
const getResultsRoute = (exam: Exam) => {
    // Check if current user is a teacher or admin
    if (
        authStore.hasRole("teacher") ||
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator")
    ) {
        return `/exams/${exam.id}/results`;
    } else if (authStore.hasRole("student")) {
        return `/student-exams/${exam.id}/results`;
    } else {
        // Default to teacher route for admin or other roles
        return `/exams/${exam.id}/results`;
    }
};

const getResultsButtonText = (exam: Exam) => {
    if (
        authStore.hasRole("teacher") ||
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator")
    ) {
        return t("exams.results");
    } else if (authStore.hasRole("student")) {
        return t("exams.myResults");
    } else {
        return t("exams.results");
    }
};
</script>
